import { apiServer } from "../config/config";
import { SyncBusinessRespose } from "../types";
import { fetchWithToken } from "./fetchWithToken";
import { NearbyBusinessesResponse } from "../types/nearby-businesses";
import { BusinessInterestType } from "../types/tagging";
import { Storage } from "../../common/storage";
export async function fetchBusinessTypes() {
  return await fetch(apiServer + "/business/load-business-types");
}

export async function fetchBusinessTypesFrontend() {
  return await fetch(apiServer + "/business/load-business-types-frontend");
}

export async function addMapperNotes(
  venueId: string,
  notes: string,
  businessReportIssueAction: string,
  token: string,
  returnOnDate?: string,
  actionOnDate?: string,
  locationDetails?: Array<{ location_type: string; location_id: string }>,
) {
  try {
    const formData = new FormData();
    formData.append("business_id", venueId);
    formData.append("notes", notes);

    // Use locationDetails array if provided, otherwise fall back to single location
    if (locationDetails && locationDetails.length > 0) {
      locationDetails.forEach((location, index) => {
        formData.append(
          `location_details[${index}][location_type]`,
          location.location_type,
        );
        formData.append(
          `location_details[${index}][location_id]`,
          location.location_id,
        );
      });
    }

    formData.append("businessReportIssueAction", businessReportIssueAction);
    if (returnOnDate) {
      formData.append("return_on_date", returnOnDate);
    }
    if (actionOnDate) {
      formData.append("action_on_date", actionOnDate);
    }
    console.log(formData);
    const response = await fetch(`${apiServer + "/review/report-venue"}`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
      },
      body: formData,
    });
    const data = await response.json();
    console.log(data);
    if (data.status === "error") {
      return data.message;
    } else {
      return data.data;
    }
  } catch (error: any) {
    return error.message;
  }
}

export async function closeMapperNote(issueId: string, accessToken: string) {
  try {
    const formData = new FormData();
    formData.append("issueId", issueId);
    const response = await fetch(
      `${apiServer + "/business/close-mapper-note"}`,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + accessToken,
        },
        body: formData,
      },
    );
    const data = await response.json();
    if (data.status === "error") {
      return data.message;
    } else {
      return data;
    }
  } catch (error: any) {
    console.log(`closeMapperNote error: ${error}`);
    return error.message;
  }
}

export async function syncBusiness(businessId: string, accessToken: string) {
  const formData = new FormData();
  formData.append("business_id", businessId);
  const response = await fetchWithToken(apiServer + "/business/sync-business", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
  const data = await response?.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data as {
    status: string;
    message: string;
    data: SyncBusinessRespose;
  };
}

export async function trackGuideUsage(businessId: number, accessToken: string) {
  try {
    const formData = new FormData();
    formData.append("business_id", String(businessId));
    await fetchWithToken(apiServer + "/business/track-guide-usage", {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
      body: formData,
    });
  } catch (err) {
    console.warn("trackGuideUsage failed", err);
  }
}

export async function fetchNearbyBusinesses(
  businessId: string,
): Promise<NearbyBusinessesResponse> {
  const formData = new FormData();
  formData.append("business_id", businessId);

  // Attach the bearer token when logged in. The endpoint enforces area-map visibility:
  // 'sociability' maps require a logged-in user (backend resolveOptionalApiUser reads this
  // header), so a tokenless call 403s for those. Sent optionally — anonymous users still get
  // 'public'/'private_link' maps.
  const token = Storage.getCurrentUserToken();
  const response = await fetch(
    `${apiServer}/business/near-by-businesses-details`,
    {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
    },
  );

  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }

  // Consumers take a `Venue`, but this endpoint answers closer to the raw
  // business row than /business/search does — hence the `is_complex` patch
  // below, and hence `publicUrl` not being something we can count on. It is
  // what the native details screen keys its fetch on, so it is filled in here
  // rather than left to each call site; apps/web already works around the same
  // gap by fetching these venues by `business.slug`
  // (WhatsNearby/NearbyVenues.tsx), which is the last fallback used here.
  if (data.data?.nearbyBusinessList) {
    data.data.nearbyBusinessList = data.data.nearbyBusinessList.map(
      (v: any) => {
        if (v?.business) {
          v.business.isComplex = v.business.is_complex;
          v.business.publicUrl =
            v.business.publicUrl || v.business.public_url || v.business.slug;
        }
        return v;
      },
    );
  }

  return data;
}

export async function addClaims(
  venueId: string,
  name: string,
  email: string,
  phone: string,
  description: string,
  flagVenueAccessibilityId: string | null,
  token: string,
) {
  try {
    const formData = new FormData();
    formData.append("business_id", venueId);
    formData.append("name", name);
    formData.append("email", email);
    formData.append("phone", phone);
    formData.append("description", description);
    if (flagVenueAccessibilityId) {
      formData.append("flag_venue_accessibility_id", flagVenueAccessibilityId);
    }

    console.log(formData);
    const response = await fetch(
      `${apiServer + "/business/create-business-claim"}`,
      {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
        },
        body: formData,
      },
    );
    const data = await response.json();
    return data;
  } catch (error: any) {
    return error.message;
  }
}

/**
 * A place the claimant picked from the autosuggest that we don't hold yet —
 * described to the backend instead of referenced by id.
 */
export interface SuggestedBusinessData {
  placeId: string | null;
  name: string;
  formattedAddress: string;
}

export interface CreateBusinessClaimParams {
  name: string;
  email: string;
  phone?: string;
  description?: string;
  businessRole: string;
  /** Only meaningful when `businessRole` is `other`. */
  businessRoleOther?: string;
  /**
   * The venue's id, when `place-from-google` recognised the place as one of
   * ours. Mutually exclusive with `suggestedBusiness` in practice, though the
   * endpoint tolerates both.
   */
  businessId?: string | null;
  /** Sent when the place is Google-only, i.e. there is no `businessId`. */
  suggestedBusiness?: SuggestedBusinessData | null;
  isPartOfChain?: boolean | null;
  chainName?: string;
  /** Only set when the claim follows an accessibility flag report. */
  flagVenueAccessibilityId?: string | null;
}

export interface BusinessClaimResponse {
  status: string;
  message: string;
  data?: {
    business_claim?: Record<string, any>;
    claimed_businesses?: { business_id: number; business_name: string }[];
    /** Laravel field errors, present when `message` is "Validation failed". */
    errors?: Record<string, string[]>;
  };
}

/**
 * Turns a rejection into something that names the field.
 *
 * The endpoint's `message` for a rejected claim is the bare string "Validation
 * failed" — useless on its own, since the detail that says *which* field lives
 * in `data.errors`. Flattening it here is what makes a backend rejection
 * diagnosable from the form instead of from a proxy log.
 */
function describeClaimFailure(data: BusinessClaimResponse | null): string {
  const fieldErrors = data?.data?.errors;

  if (fieldErrors) {
    const messages = Object.values(fieldErrors).flat().filter(Boolean);
    if (messages.length > 0) {
      return messages.join("\n");
    }
  }

  return data?.message || "We couldn't submit your claim. Please try again.";
}

/**
 * Creates a business claim from the onboarding flow.
 *
 * Deliberately separate from `addClaims` rather than an extension of it: that
 * one backs the venue-details claim form, which has a venue in hand and none of
 * the role, chain or suggested-place fields. Widening it to fit this form would
 * put every optional argument in the path of the older caller.
 *
 * Unlike `addClaims`, a failure **throws** instead of resolving with the error
 * message as the body — the caller needs to tell a rejected claim from an
 * accepted one before it advances the user to the confirmation screen.
 */
export async function createBusinessClaimV2(
  params: CreateBusinessClaimParams,
  token?: string | null,
): Promise<BusinessClaimResponse> {
  const formData = new FormData();

  formData.append("name", params.name);
  formData.append("email", params.email);
  formData.append("phone", params.phone ?? "");
  formData.append("description", params.description ?? "");
  formData.append("business_role", params.businessRole);

  if (params.businessRoleOther) {
    formData.append("business_role_other", params.businessRoleOther);
  }

  if (params.businessId) {
    formData.append("business_id", params.businessId);
  } else if (params.suggestedBusiness) {
    // Literal bracket keys — the backend parses these into a nested object, as
    // it does for `location_details[…]` on report-venue.
    const { placeId, name, formattedAddress } = params.suggestedBusiness;
    if (placeId) {
      formData.append("suggested_business_data[place_id]", placeId);
    }
    formData.append("suggested_business_data[name]", name);
    formData.append(
      "suggested_business_data[formatted_address]",
      formattedAddress,
    );
  }

  // Omitted rather than sent as false: the form only asks when the branch that
  // collects it is present, and an unasked question shouldn't answer itself.
  if (params.isPartOfChain !== undefined && params.isPartOfChain !== null) {
    formData.append(
      "is_your_business_part_of_chain",
      params.isPartOfChain ? "1" : "0",
    );
    if (params.isPartOfChain && params.chainName) {
      formData.append("chain_name", params.chainName);
    }
  }

  if (params.flagVenueAccessibilityId) {
    formData.append(
      "flag_venue_accessibility_id",
      params.flagVenueAccessibilityId,
    );
  }

  const response = await fetch(`${apiServer}/business/create-business-claim`, {
    method: "POST",
    // Signed-out claimants send no header at all rather than `Bearer ` with
    // nothing after it, which some middleware treats as a malformed token
    // rather than an absent one.
    headers: token ? { Authorization: "Bearer " + token } : undefined,
    body: formData,
  });

  const data = (await response.json().catch(() => null)) as
    | BusinessClaimResponse
    | null;

  if (!response.ok || !data || data.status === "error") {
    throw new Error(describeClaimFailure(data));
  }

  return data;
}

export async function loadBusinessInterests(
  token: string,
  includePhotos: boolean = true,
) {
  const queryParams = new URLSearchParams({
    sort_by: "created_at",
    sort_order: "desc",
    include_photos: includePhotos.toString(),
  });

  // Adding query parameters to the URL
  const url = `${apiServer}/user/business-interests?${queryParams.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + token,
      },
    });
    const data = await response.json();
    return data as {
      status: string;
      message: string;
      data: BusinessInterestType[];
    };
  } catch (error: any) {
    console.log(`loadBusinessInterests error: ${error}`);
    return {
      status: "error",
      message: error.message,
      data: [],
    };
  }
}

export async function syncBusinessInterests(
  businessId: string,
  interestType: string,
  notifyAccessibilityInformation: boolean,
  notifyLiveAlert: boolean,
  token: string,
  action: "create" | "update" | "delete",
  interestId?: string,
) {
  try {
    const formData = new FormData();
    formData.append("business_id", businessId);
    formData.append("interested_type", interestType);
    formData.append(
      "notify_accessibility_information",
      notifyAccessibilityInformation ? "1" : "0",
    );
    formData.append("notify_live_alert", notifyLiveAlert ? "1" : "0");
    if (interestId) {
      formData.append("id", interestId);
    }
    formData.append("action", action);
    console.log(formData);
    const response = await fetch(`${apiServer}/business/sync-subscription`, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + token,
      },
      body: formData,
    });
    const data = await response.json();
    console.log(JSON.stringify(data));
    return data;
  } catch (error: any) {
    return error.message;
  }
}

export interface MyVenuesResponse {
  status: string;
  data: {
    id: number;
    slug: string;
    name: string;
    address: string;
    dashboard_urls: string[] | null;
  }[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

export async function fetchMyVenues(
  token: string,
  page: number = 1,
  per_page: number = 10,
): Promise<MyVenuesResponse> {
  const response = await fetchWithToken(
    `${apiServer}/business/my-venues?page=${page}&per_page=${per_page}`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  if (!response || !response.ok) {
    throw new Error("Failed to fetch venues");
  }

  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message || "Unknown error");
  }
  return data;
}

export type BusinessOpeningHourInput = {
  day: number;
  open_time: string;
  close_time: string;
};

export type UpdateBusinessGeneralDetailsPayload = {
  name: string;
  address: string;
  phone?: string | null;
  website?: string | null;
  description?: string;
  opening_hours?: BusinessOpeningHourInput[];
};

export type UpdateBusinessGeneralDetailsResponse = {
  status: string;
  message: string;
  data: { business_id: number } | [];
};

export async function updateBusinessGeneralDetails(
  businessId: number | string,
  payload: UpdateBusinessGeneralDetailsPayload,
  accessToken: string,
): Promise<UpdateBusinessGeneralDetailsResponse> {
  const response = await fetchWithToken(
    `${apiServer}/business/${businessId}/info`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response) {
    throw new Error("Failed to update business details");
  }

  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message || "Failed to update business details");
  }
  return data as UpdateBusinessGeneralDetailsResponse;
}
